// src/utils/folioFacturacionUtils.js
// Utilidades para la generación inteligente y nomenclatura de folios irrepetibles:
// Fórmula: [PREFIJO]-[AÑO]-[INICIALES_CLIENTE]-[CONSECUTIVO]
// Ejemplos: PRE-2026-AVR-036, REM-2026-FIO-001, NP-2026-CSF4-001

/**
 * Extrae siglas representativas e identificables del cliente o condominio
 * descartando palabras genéricas (Fraccionamiento, Condominio, SA de CV, etc.)
 */
export const extraerSiglasCliente = (cliente) => {
    const texto = typeof cliente === 'string' 
        ? cliente 
        : (cliente?.nombre || cliente?.contacto || cliente?.razonSocial || '');
    
    if (!texto || !texto.trim()) return 'GEN';

    // 1. Quitar acentos y caracteres especiales
    const normalizado = texto
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

    // 2. Stopwords a descartar (términos jurídicos y condominales genéricos)
    const palabrasIgnoradas = new Set([
        'FRACCIONAMIENTO', 'FRACC', 'CONDOMINIO', 'CONDOMINIOS', 'RESIDENCIAL',
        'COLONIA', 'COL', 'PRIVADA', 'PRIV', 'DE', 'DEL', 'LA', 'LAS', 'LOS',
        'EL', 'Y', 'E', 'EN', 'SA', 'CV', 'SC', 'S.A.', 'C.V.', 'S.C.',
        'ASOCIACION', 'COMITE', 'ADMINISTRACION', 'ADMINISTRADORA', 'SR', 'SRA', 'LIC', 'ING'
    ]);

    // 3. Extraer palabras alfanuméricas
    const tokens = normalizado.match(/[A-Z0-9]+/g) || [];
    const tokensUtiles = tokens.filter(t => !palabrasIgnoradas.has(t) && t.length > 0);

    // Si no quedaron tokens útiles, tomar los originales ignorando solo preposiciones
    if (tokensUtiles.length === 0) {
        const fallback = tokens.filter(t => !['DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'E'].includes(t));
        if (fallback.length > 0) {
            return fallback.map(t => t[0]).slice(0, 4).join('');
        }
        return 'GEN';
    }

    // Caso A: Si hay 2 o más palabras clave (ej: "Azua Villa Real" -> AVR, "Claustros Santa Fe 4" -> CSF4)
    if (tokensUtiles.length >= 2) {
        let siglas = '';
        for (const tok of tokensUtiles) {
            // Si el token es un número puro o alfanumérico corto (ej. "4"), conservarlo completo
            if (/^\d+$/.test(tok)) {
                siglas += tok;
            } else if (tok !== 'QRO' && tok !== 'MEX') { // Omitir sufijos de estado si hay más palabras
                siglas += tok[0];
            } else if (tokensUtiles.length === 2) {
                siglas += tok[0];
            }
        }
        return siglas.slice(0, 5) || 'GEN';
    }

    // Caso B: Una sola palabra clave (ej: "Fiorelo" -> FIO, "Opsa" -> OPSA)
    const palabraUnica = tokensUtiles[0];
    if (palabraUnica.length <= 4) {
        return palabraUnica;
    }
    // Si es larga, tomar las 3 primeras letras (ej. FIORELO -> FIO)
    return palabraUnica.slice(0, 3);
};

/**
 * Genera el folio completo según la categoría y configuración activa:
 * [PREFIJO]-[AÑO]-[INICIALES]-[CONSECUTIVO]
 */
export const generarFolioInteligente = ({
    tipo = 'presupuesto',
    configuracion = {},
    cliente = null,
    fechaEmision = null,
    consecutivoPersonalizado = null
}) => {
    const tipoNorm = (tipo || 'presupuesto').toLowerCase();

    // 1. Prefijo y consecutivo según categoría
    let prefijoRaw = 'PRE-';
    let consecutivoDef = 36;
    let ceros = 3;

    if (tipoNorm === 'pago') {
        prefijoRaw = configuracion.prefijoPago || 'NP-';
        consecutivoDef = configuracion.consecutivoPago !== undefined ? configuracion.consecutivoPago : 1;
        ceros = Number(configuracion.cerosPago) || 3;
    } else if (tipoNorm === 'remision') {
        prefijoRaw = configuracion.prefijoRemision || 'REM-';
        consecutivoDef = configuracion.consecutivoRemision !== undefined ? configuracion.consecutivoRemision : 1;
        ceros = Number(configuracion.cerosRemision) || 3;
    } else {
        prefijoRaw = configuracion.prefijoPresupuesto || 'PRE-';
        consecutivoDef = configuracion.consecutivoPresupuesto !== undefined ? configuracion.consecutivoPresupuesto : 36;
        ceros = Number(configuracion.cerosPresupuesto) || 3;
    }

    // Limpiar guiones finales del prefijo para evitar dobles guiones
    const prefijoLimpio = String(prefijoRaw).trim().replace(/[-_]+$/, '') || (tipoNorm === 'pago' ? 'NP' : tipoNorm === 'remision' ? 'REM' : 'PRE');

    // 2. Año (4 dígitos) a partir de fechaEmision o año actual
    let anio = new Date().getFullYear();
    if (fechaEmision) {
        try {
            const parsedYear = new Date(fechaEmision).getFullYear();
            if (!isNaN(parsedYear) && parsedYear >= 2020 && parsedYear <= 2040) {
                anio = parsedYear;
            }
        } catch {
            anio = new Date().getFullYear();
        }
    }

    // 3. Siglas del Cliente
    const siglas = extraerSiglasCliente(cliente);

    // 4. Consecutivo numérico con ceros a la izquierda
    const num = Number(consecutivoPersonalizado !== null && consecutivoPersonalizado !== undefined ? consecutivoPersonalizado : consecutivoDef) || 1;
    const numStr = String(num).padStart(ceros, '0');

    // 5. Ensamblado final sin duplicación de separadores
    return `${prefijoLimpio}-${anio}-${siglas}-${numStr}`;
};

/**
 * Actualiza las siglas del cliente dentro de un folio existente manteniendo prefijo, año y consecutivo.
 * Si el folio no coincide con la estructura compuesta, genera uno nuevo inteligente.
 */
export const actualizarSiglasEnFolio = (folioActual, nuevoCliente, tipo = 'presupuesto', configuracion = {}, fechaEmision = null) => {
    const nuevasSiglas = extraerSiglasCliente(nuevoCliente);
    if (!folioActual || typeof folioActual !== 'string') {
        return generarFolioInteligente({ tipo, configuracion, cliente: nuevoCliente, fechaEmision });
    }

    // Detectar si el folio actual tiene formato: PREFIJO-AÑO-SIGLAS-NUMERO
    const partes = folioActual.split('-');
    if (partes.length >= 4) {
        // partes[0] = prefijo, partes[1] = año, partes[2] = siglas antiguas, partes[3] = consecutivo
        partes[2] = nuevasSiglas;
        return partes.join('-');
    }

    // Si tiene otro formato más corto (ej. DH-PR-033), regenerarlo completo
    return generarFolioInteligente({ tipo, configuracion, cliente: nuevoCliente, fechaEmision });
};

/**
 * Genera el ejemplo dinámico para la pestaña de Folios & Cuentas
 */
export const formatearFolioEjemploConfig = (prefijo, consecutivo, ceros, tipo = 'presupuesto') => {
    const anio = new Date().getFullYear();
    const prefijoLimpio = String(prefijo || '').trim().replace(/[-_]+$/, '') || (tipo === 'pago' ? 'NP' : tipo === 'remision' ? 'REM' : 'PRE');
    const numStr = String(consecutivo || 1).padStart(Number(ceros) || 3, '0');
    
    const ejemploSiglas = tipo === 'pago' ? 'CSF4' : tipo === 'remision' ? 'FIO' : 'AVR';
    return `${prefijoLimpio}-${anio}-${ejemploSiglas}-${numStr}`;
};
